<!-- src/components/admin/PhysicalCardsAdmin.vue -->
<!-- VERSION COMPLÈTE AVEC EXPORT ASYNCHRONE PAR EMAIL -->
<template>
  <avs-card class="pb-4">
    <template v-slot:title>
      <v-toolbar flat class="mt-4 bgTable" elevation="0">
        <!-- Recherche -->
        <v-text-field
          class="mr-4"
          dense clearable hide-details
          prepend-inner-icon="mdi-magnify"
          label="Rechercher carte ou utilisateur"
          v-model="search"
          @input="onSearchChange"
          style="max-width: 300px"
        />
        
        <!-- Filtre statut -->
        <v-select
          class="mr-4"
          dense clearable hide-details
          :items="statusOptions"
          v-model="statusFilter"
          item-text="text" item-value="value"
          label="Statut"
          @change="onFiltersChange"
          style="max-width: 200px"
        >
          <template v-slot:selection="{ item }">
            <v-chip x-small :color="statusColor(item.value)" text-color="white">
              {{ item.text }}
            </v-chip>
          </template>
        </v-select>
        
        <!-- Filtre actif -->
        <v-select
          class="mr-4"
          dense clearable hide-details
          :items="activeOptions"
          v-model="activeFilter"
          item-text="text" item-value="value"
          label="État"
          @change="onFiltersChange"
          style="max-width: 150px"
        />
        
        <v-spacer />
        
        <!-- ============================================ -->
        <!-- NOUVEAU BOUTON : Exporter toutes les cartes -->
        <!-- ============================================ -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              small
              color="success"
              class="mr-2 export-all-btn"
              @click="exportAllActiveCards"
              :loading="exportingAll"
              :disabled="(stats.available || 0) === 0"
            >
              <v-icon left small>mdi-email-fast</v-icon>
              Exporter tout
              <v-chip
                x-small
                color="white"
                text-color="success"
                class="ml-2"
                v-if="(stats.available || 0) > 0"
              >
                {{ stats.available }}
              </v-chip>
            </v-btn>
          </template>
          <span>
            Exporter toutes les cartes actives<br>
            et recevoir le PDF par email
          </span>
        </v-tooltip>
        
        <!-- Boutons actions existants -->
        <v-btn small color="secondary" class="mr-2" @click="openGenerateDialog">
          <v-icon left small>mdi-plus-circle-multiple</v-icon>
          Générer cartes
        </v-btn>
        
        <v-btn
          small outlined color="purple" class="mr-2"
          @click="generatePDF"
          :loading="generatingPDF"
          :disabled="selectedCards.length === 0"
        >
          <v-icon left small>mdi-printer</v-icon>
          Imprimer ({{ selectedCards.length }})
        </v-btn>
        
        <v-btn small outlined color="primary" @click="fetchCardsReset" :loading="loading && page === 1">
          <v-icon left small>mdi-refresh</v-icon>
          Actualiser
        </v-btn>
      </v-toolbar>
      
      <!-- ============================================ -->
      <!-- ALERTE D'EXPORT EN COURS (NOUVEAU)          -->
      <!-- ============================================ -->
      <v-expand-transition>
        <v-alert
          v-if="exportTaskPending"
          type="info"
          dense
          outlined
          dismissible
          class="mx-4 mt-3 export-alert"
          @input="exportTaskPending = false"
        >
          <div class="d-flex align-center">
            <v-progress-circular
              indeterminate
              size="20"
              width="2"
              color="info"
              class="mr-3"
            />
            <div>
              <strong>Export en cours...</strong><br>
              <span class="text-caption">
                Le PDF de <strong>{{ exportTaskCardCount }}</strong> cartes sera envoyé à 
                <strong>{{ exportTaskEmail }}</strong>
              </span>
            </div>
          </div>
        </v-alert>
      </v-expand-transition>
      
      <!-- Statistiques -->
      <v-row class="mx-4 my-2">
        <v-col cols="12" sm="3">
          <v-card outlined>
            <v-card-text class="d-flex align-center pa-3">
              <v-icon color="success" large class="mr-3">mdi-check-circle</v-icon>
              <div>
                <div class="text-caption grey--text">Disponibles</div>
                <div class="text-h6 font-weight-bold">{{ stats.available || 0 }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card outlined>
            <v-card-text class="d-flex align-center pa-3">
              <v-icon color="primary" large class="mr-3">mdi-account-check</v-icon>
              <div>
                <div class="text-caption grey--text">Assignées</div>
                <div class="text-h6 font-weight-bold">{{ stats.assigned || 0 }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card outlined>
            <v-card-text class="d-flex align-center pa-3">
              <v-icon color="error" large class="mr-3">mdi-close-circle</v-icon>
              <div>
                <div class="text-caption grey--text">Bloquées</div>
                <div class="text-h6 font-weight-bold">{{ stats.blocked || 0 }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card outlined>
            <v-card-text class="d-flex align-center pa-3">
              <v-icon color="warning" large class="mr-3">mdi-alert-circle</v-icon>
              <div>
                <div class="text-caption grey--text">Perdues</div>
                <div class="text-h6 font-weight-bold">{{ stats.lost || 0 }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
    
    <template v-slot:content>
      <v-data-table
        v-model="selectedCards"
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        :items-per-page="-1"
        hide-default-footer
        dense
        show-select
        item-key="id"
        class="elevation-0 ma-6 bgTable rounded-lg"
      >
        <!-- CODE -->
        <template #item.code="{ item }">
          <div class="d-flex align-center">
            <v-icon small class="mr-2" color="grey darken-1">mdi-qrcode</v-icon>
            <code class="text-caption">{{ formatCode(item.code) }}</code>
            <v-btn icon x-small class="ml-1" @click="copyCode(item.code)">
              <v-icon x-small>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </template>
        
        <!-- STATUT -->
        <template #item.status="{ item }">
          <v-chip
            x-small
            :color="statusColor(item.status)"
            text-color="white"
            class="font-weight-medium"
          >
            <v-icon left x-small>{{ statusIcon(item.status) }}</v-icon>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        
        <!-- ÉTAT ACTIF -->
        <template #item.is_active="{ item }">
          <v-chip
            x-small
            :color="item.is_active ? 'success' : 'grey'"
            text-color="white"
          >
            <v-icon left x-small>
              {{ item.is_active ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>
        
        <!-- UTILISATEUR -->
        <template #item.user="{ item }">
          <div v-if="item.user" class="d-flex align-center">
            <v-avatar size="24" color="primary" class="mr-2">
              <span class="white--text text-caption">
                {{ getUserInitials(item.user) }}
              </span>
            </v-avatar>
            <span class="text-capitalize">{{ item.user.fullname || item.user.username }}</span>
          </div>
          <span v-else class="grey--text">-</span>
        </template>
        
        <!-- DATE ATTRIBUTION -->
        <template #item.assigned_at="{ item }">
          <div v-if="item.assigned_at" class="text-no-wrap">
            <div class="text-caption">{{ formatDate(item.assigned_at) }}</div>
            <div class="text-caption grey--text">{{ formatTime(item.assigned_at) }}</div>
          </div>
          <span v-else class="grey--text">-</span>
        </template>
        
        <!-- DATE DÉSACTIVATION -->
        <template #item.disabled_at="{ item }">
          <div v-if="item.disabled_at" class="text-no-wrap text-caption">
            {{ formatDate(item.disabled_at) }}
          </div>
          <span v-else class="grey--text">-</span>
        </template>
        
        <!-- ACTIONS -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-center action-buttons">
            <!-- Assigner -->
            <v-tooltip bottom v-if="item.status === 'available'">
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-account-plus'"
                  :spantext="$t('Assigner')"
                  label
                  fab
                  small
                  color="primary"
                  @click.stop="openAssignDialog(item)"
                />
              </template>
            </v-tooltip>
            
            <!-- Désactiver -->
            <v-tooltip bottom v-if="item.is_active">
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-cancel'"
                  :spantext="$t('Désactiver')"
                  label
                  fab
                  small
                  color="warning"
                  @click.stop="deactivateCard(item)"
                />
              </template>
            </v-tooltip>
            
            <!-- Bloquer -->
            <v-tooltip bottom v-if="item.status !== 'blocked'">
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-lock'"
                  :spantext="$t('Bloquer')"
                  label
                  fab
                  small
                  color="error"
                  @click.stop="blockCard(item)"
                />
              </template>
            </v-tooltip>
            
            <!-- Marquer perdue -->
            <v-tooltip bottom v-if="item.status === 'assigned'">
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-alert-circle'"
                  :spantext="$t('Marquer perdue')"
                  label
                  fab
                  small
                  color="orange"
                  @click.stop="markLost(item)"
                />
              </template>
            </v-tooltip>
            
            <!-- Prévisualiser -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-eye'"
                  :spantext="$t('Prévisualiser')"
                  label
                  fab
                  small
                  color="secondary"
                  @click.stop="previewCard(item)"
                />
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
      
      <!-- Loader pour le chargement de plus de données -->
      <div v-if="loadingMore" class="py-4 d-flex justify-center">
        <v-progress-circular indeterminate color="primary" size="40" />
        <span class="ml-3 grey--text">Chargement...</span>
      </div>
      
      <!-- Fin de liste -->
      <div
        v-if="!loading && !loadingMore && !hasMore && items.length > 0"
        class="text-center py-4"
      >
        <v-icon color="success" size="32">mdi-check-circle-outline</v-icon>
        <div class="text-body-1 font-weight-medium mt-2">
          Toutes les cartes sont affichées
        </div>
        <div class="text-caption grey--text">
          {{ items.length }} carte(s) au total
        </div>
      </div>
      
      <!-- Message si aucune carte -->
      <div
        v-if="!loading && items.length === 0"
        class="text-center py-8"
      >
        <v-icon color="grey" size="64">mdi-cards-outline</v-icon>
        <div class="text-h6 grey--text mt-4">Aucune carte trouvée</div>
        <div class="text-caption grey--text">
          Utilisez les filtres ou générez de nouvelles cartes
        </div>
      </div>
      
      <!-- Sentinelle pour l'infinite scroll -->
      <div 
        ref="sentinel" 
        class="sentinel"
        style="height: 20px; width: 100%; margin: 20px 0;"
      ></div>
      
      <!-- Dialogs -->
      <v-dialog v-model="assignDialog.open" max-width="600px" persistent>
        <AssignCardDialog
          v-if="assignDialog.open"
          :card="assignDialog.card"
          @cancel="assignDialog.open = false"
          @assigned="onCardAssigned"
        />
      </v-dialog>
      
      <v-dialog v-model="generateDialog.open" max-width="500px" persistent>
        <GenerateCardsDialog
          v-if="generateDialog.open"
          @cancel="generateDialog.open = false"
          @generated="onCardsGenerated"
        />
      </v-dialog>
      
      <v-dialog v-model="previewDialog.open" max-width="400px">
        <CardPreviewDialog
          v-if="previewDialog.open"
          :card="previewDialog.card"
          @close="previewDialog.open = false"
        />
      </v-dialog>
      
      <!-- ============================================ -->
      <!-- DIALOG CONFIRMATION EXPORT (NOUVEAU)        -->
      <!-- ============================================ -->
      <v-dialog v-model="exportConfirmDialog" max-width="500px" persistent>
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="success" class="mr-2">mdi-email-fast</v-icon>
            Exporter toutes les cartes actives
          </v-card-title>
          
          <v-card-text>
            <v-alert type="info" color="secondary" dense outlined class="mb-4">
              <strong>{{ stats.available || 0 }}</strong> cartes actives seront exportées.
            </v-alert>
            
            <p class="mb-2">
              Le PDF sera généré en arrière-plan et envoyé à votre adresse email :
            </p>
            
            <v-chip color="primary" outlined class="mb-4">
              <v-icon left small>mdi-email</v-icon>
              {{ currentUserEmail }}
            </v-chip>
            
            <v-alert type="warning" dense text class="mb-0">
              Cette opération peut prendre quelques minutes selon le nombre de cartes.
            </v-alert>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="exportConfirmDialog = false">
              Annuler
            </v-btn>
            <v-btn
              color="success"
              @click="confirmExport"
              :loading="exportingAll"
            >
              <v-icon left>mdi-email-fast</v-icon>
              Exporter et envoyer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import PhysicalCardService from "@/services/PhysicalCardService"
import AssignCardDialog from "@/components/ri3/admin/cards/AssignCardDialog.vue"
import GenerateCardsDialog from "@/components/ri3/admin/cards/GenerateCardsDialog.vue"
import CardPreviewDialog from "@/components/ri3/admin/cards/CardPreviewDialog.vue"
import debounce from "lodash/debounce"

export default {
  name: "PhysicalCardsAdmin",
  
  components: {
    AssignCardDialog,
    GenerateCardsDialog,
    CardPreviewDialog,
  },
  
  data() {
    return {
      search: "",
      statusFilter: null,
      activeFilter: null,
      loading: false,
      loadingMore: false,
      generatingPDF: false,
      items: [],
      selectedCards: [],
      page: 1,
      hasMore: true,
      observer: null,
      
      // ============================================
      // NOUVELLES DONNÉES POUR L'EXPORT ASYNCHRONE
      // ============================================
      exportingAll: false,
      exportTaskPending: false,
      exportTaskId: null,
      exportTaskEmail: '',
      exportTaskCardCount: 0,
      exportConfirmDialog: false,
      
      stats: {
        available: 0,
        assigned: 0,
        blocked: 0,
        lost: 0,
        active: 0,
      },
      
      assignDialog: {
        open: false,
        card: null,
      },
      
      generateDialog: {
        open: false,
      },
      
      previewDialog: {
        open: false,
        card: null,
      },
      
      headers: [
        { text: "Code", value: "code", width: "20%" },
        { text: "Statut", value: "status", width: "12%" },
        { text: "État", value: "is_active", width: "10%" },
        { text: "Utilisateur", value: "user", width: "20%" },
        { text: "Attribuée le", value: "assigned_at", width: "13%" },
        { text: "Désactivée le", value: "disabled_at", width: "13%" },
        { text: "", value: "actions", sortable: false, align: "center", width: "12%" },
      ],
      
      statusOptions: [
        { text: "Toutes", value: null },
        { text: "Disponible", value: "available" },
        { text: "Assignée", value: "assigned" },
        { text: "Perdue", value: "lost" },
        { text: "Bloquée", value: "blocked" },
      ],
      
      activeOptions: [
        { text: "Tous", value: null },
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
    }
  },
  
  computed: {
    // ============================================
    // NOUVEAU COMPUTED : EMAIL UTILISATEUR COURANT
    // ============================================
    currentUserEmail() {
      return this.$store?.state?.app.user?.email || 
             this.$store?.getters?.['auth/user']?.email ||
             'votre email'
    },
  },
  
  created() {
    this.debouncedFetch = debounce(this.fetchCardsReset, 300)
  },
  
  mounted() {
    this.applyRouteFilters()
    this.fetchCardsReset()
    this.fetchStats()
  },
  
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteFilters()
        this.fetchCardsReset()
      },
      deep: false,
    },
  },
  
  beforeDestroy() {
    this.destroyObserver()
  },
  
  methods: {
    // ============================================
    // NOUVELLES MÉTHODES POUR L'EXPORT ASYNCHRONE
    // ============================================
    
    /**
     * Ouvre la dialog de confirmation d'export
     */
    exportAllActiveCards() {
      if ((this.stats.available || 0) === 0) {
        this.$store.commit("app/message", {
          msg: "Aucune carte active à exporter",
          color: "warning",
        })
        return
      }
      this.exportConfirmDialog = true
    },
    
    /**
     * Confirme et lance l'export asynchrone
     */
    async confirmExport() {
      this.exportingAll = true
      
      try {
        const response = await PhysicalCardService.exportAllActiveCards(this, {
          // Vous pouvez ajouter des filtres ici si nécessaire
          // status: this.statusFilter,
        })
        
        // Fermer la dialog de confirmation
        this.exportConfirmDialog = false
        
        // Stocker les infos pour l'affichage de l'alerte
        this.exportTaskId = response.task_id
        this.exportTaskEmail = response.email
        this.exportTaskCardCount = response.card_count
        this.exportTaskPending = true
        
        // Message de succès
        this.$store.commit("app/message", {
          msg: `Export lancé ! Le PDF de ${response.card_count} cartes sera envoyé à ${response.email}`,
          color: "success",
          timeout: 8000,
        })
        
      } catch (error) {
        console.error('Erreur export:', error)
        this.$store.commit("app/message", {
          msg: error.message || "Erreur lors du lancement de l'export",
          color: "error",
        })
      } finally {
        this.exportingAll = false
      }
    },
    
    // ============================================
    // MÉTHODES EXISTANTES (inchangées)
    // ============================================
    
    formatCode(code) {
      return code ? code.substring(0, 8).toUpperCase() : '-'
    },
    
    copyCode(code) {
      navigator.clipboard.writeText(code)
      this.$store.commit("app/message", {
        msg: "Code copié",
        color: "success"
      })
    },
    
    formatDate(datetime) {
      if (!datetime) return '-';
      const d = new Date(datetime);
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    
    formatTime(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime);
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    },
    
    getUserInitials(user) {
      if (!user) return 'U';
      const name = user.fullname || user.username || 'U';
      const parts = name.split(' ').filter(Boolean);
      if (parts.length === 0) return 'U';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    
    statusColor(status) {
      return {
        available: "success",
        assigned: "primary",
        lost: "warning",
        blocked: "error",
      }[status] || "grey";
    },
    
    statusIcon(status) {
      return {
        available: "mdi-check-circle",
        assigned: "mdi-account-check",
        lost: "mdi-alert-circle",
        blocked: "mdi-lock",
      }[status] || "mdi-help-circle";
    },
    
    statusLabel(status) {
      return {
        available: "Disponible",
        assigned: "Assignée",
        lost: "Perdue",
        blocked: "Bloquée",
      }[status] || status;
    },
    
    applyRouteFilters() {
      const q = this.$route?.query || {}
      this.statusFilter = q.status || null
      this.activeFilter = q.is_active === 'true' ? true : q.is_active === 'false' ? false : null
      this.search = q.search || ''
    },
    
    params() {
      const p = {}
      if (this.search) p.search = this.search
      if (this.statusFilter) p.status = this.statusFilter
      if (this.activeFilter !== null) p.is_active = this.activeFilter
      p.ordering = "-created_at"
      p.page = this.page
      return p
    },
    
    async fetchCards({ reset = false } = {}) {
      if (reset) {
        this.loading = true
      } else {
        this.loadingMore = true
      }
      
      try {
        const data = await PhysicalCardService.getCards(this, this.params())
        const results = Array.isArray(data) ? data : (data.results || [])
        
        if (reset) {
          this.items = results
        } else {
          this.items = [...this.items, ...results]
        }
        
        this.hasMore = !!(data && data.next)
        
        if (reset) {
          this.$nextTick(() => {
            this.destroyObserver()
            this.createObserver()
          })
        }
      } catch (e) {
        console.error('Erreur chargement cartes:', e)
        this.$store.commit("app/message", {
          msg: "Erreur de chargement",
          color: "error",
        });
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    
    async fetchStats() {
      try {
        const data = await PhysicalCardService.getStats(this)
        this.stats = {
          available: data.available || 0,
          assigned: data.assigned || 0,
          blocked: data.blocked || 0,
          lost: data.lost || 0,
          active: data.active || 0,
        }
      } catch (e) {
        console.error('Erreur stats:', e)
      }
    },
    
    onSearchChange() {
      this.syncRoute()
      this.debouncedFetch()
    },
    
    syncRoute() {
      const q = {
        ...(this.search ? { search: this.search } : {}),
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
      }
      if (this.activeFilter !== null) q.is_active = String(this.activeFilter)
      this.$router.replace({ query: q }).catch(() => {})
    },
    
    onFiltersChange() {
      this.syncRoute()
      this.fetchCardsReset()
    },
    
    fetchCardsReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.selectedCards = []
      this.fetchCards({ reset: true })
      this.fetchStats()
    },
    
    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      
      this.destroyObserver()
      
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.loading && !this.loadingMore && this.hasMore) {
              this.loadMore()
            }
          })
        },
        { root: null, rootMargin: '100px', threshold: 0 }
      )
      
      this.observer.observe(el)
    },
    
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    
    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetchCards({ reset: false })
    },
    
    openAssignDialog(card) {
      this.assignDialog.card = card
      this.assignDialog.open = true
    },
    
    openGenerateDialog() {
      this.generateDialog.open = true
    },
    
    async onCardAssigned() {
      this.assignDialog.open = false
      await this.fetchCardsReset()
      this.$store.commit("app/message", {
        msg: "Carte assignée avec succès",
        color: "success"
      })
    },
    
    async onCardsGenerated() {
      this.generateDialog.open = false
      await this.fetchCardsReset()
      this.$store.commit("app/message", {
        msg: "Cartes générées avec succès",
        color: "success"
      })
    },
    
    async deactivateCard(card) {
      if (!confirm('Confirmer la désactivation de cette carte ?')) return
      
      try {
        await PhysicalCardService.deactivateCard(this, card.id)
        await this.fetchCardsReset()
        this.$store.commit("app/message", {
          msg: "Carte désactivée",
          color: "success"
        })
      } catch (e) {
        this.$store.commit("app/message", {
          msg: "Erreur lors de la désactivation",
          color: "error"
        })
      }
    },
    
    async blockCard(card) {
      if (!confirm('Confirmer le blocage de cette carte ?')) return
      
      try {
        await PhysicalCardService.blockCard(this, card.id)
        await this.fetchCardsReset()
        this.$store.commit("app/message", {
          msg: "Carte bloquée",
          color: "success"
        })
      } catch (e) {
        this.$store.commit("app/message", {
          msg: "Erreur lors du blocage",
          color: "error"
        })
      }
    },
    
    async markLost(card) {
      if (!confirm('Confirmer que cette carte est perdue ?')) return
      
      try {
        await PhysicalCardService.markLost(this, card.id)
        await this.fetchCardsReset()
        this.$store.commit("app/message", {
          msg: "Carte marquée comme perdue",
          color: "success"
        })
      } catch (e) {
        this.$store.commit("app/message", {
          msg: "Erreur",
          color: "error"
        })
      }
    },
    
    previewCard(card) {
      this.previewDialog.card = card
      this.previewDialog.open = true
    },
    
    async generatePDF() {
      if (this.selectedCards.length === 0) {
        this.$store.commit("app/message", {
          msg: "Sélectionnez au moins une carte",
          color: "warning"
        })
        return
      }
      
      this.generatingPDF = true
      
      try {
        const cardIds = this.selectedCards.map(c => c.id)
        const blob = await PhysicalCardService.generatePDF(this, cardIds)
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `cartes_physiques_${new Date().getTime()}.pdf`
        link.click()
        
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 100)
        
        this.$store.commit("app/message", {
          msg: "PDF généré avec succès",
          color: "success"
        })
      } catch (e) {
        console.error('Erreur PDF:', e)
        this.$store.commit("app/message", {
          msg: e.message || "Erreur lors de la génération du PDF",
          color: "error"
        })
      } finally {
        this.generatingPDF = false
      }
    }
  },
}
</script>

<style scoped>
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.action-buttons .v-btn {
  margin: 0 2px;
}

.action-buttons .v-btn:hover {
  transform: scale(1.1);
  transition: transform 0.2s;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

.sentinel {
  background: transparent;
}

/* Style pour le bouton d'export */
.export-all-btn {
  transition: all 0.3s ease;
}

.export-all-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Animation pour l'alerte d'export */
.export-alert {
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>